import { notifications } from '@mantine/notifications';
import { IconMoodPuzzled } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Endpoints } from '../../apis';
import { isNetworkError } from '../../libs/connectionNotifier';
import { keepIfSameProject } from '../../utils/projects';
import { useActiveProjectScope, useProductAccess } from '../useActiveProject';

export const INSTANCE_LIST_QUERY_KEY = ['api.v1.instances.list'] as const;

interface UseInstanceListQueryOptions {
    refetchInterval?: number | false;
    /** Overrides the client default (5 min); pass 0 to refetch on mount, e.g. in a picker. */
    staleTime?: number;
}

export function useInstanceListQuery({
    refetchInterval = false,
    staleTime,
}: UseInstanceListQueryOptions = {}) {
    const { t } = useTranslation('notifications');
    const hasShownError = useRef(false);
    const { activeProjectId, isResolved } = useActiveProjectScope();
    const hasNoAccess = useProductAccess('vm') === 'none';

    const query = useQuery({
        queryKey: [...INSTANCE_LIST_QUERY_KEY, activeProjectId],
        queryFn: async () => Endpoints.v1.Instances.getInstances(),
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchInterval,
        staleTime,
        gcTime: 1000 * 60 * 60 * 24,
        placeholderData: keepIfSameProject(activeProjectId),
        enabled: isResolved && !hasNoAccess,
    });

    const { data, isPending, error, refetch, isRefetching } = query;

    useEffect(() => {
        if (error && !isNetworkError(error) && !hasShownError.current) {
            hasShownError.current = true;
            notifications.show({
                title: t('instance.queryFailedTitle'),
                message: t('instance.queryFailedMessage'),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });
        }

        if (!error) {
            hasShownError.current = false;
        }
    }, [error, t]);

    const collective = useMemo(() => {
        return (data ?? []).reduce(
            (summary, instance) => {
                summary.instances += 1;
                summary.vcpus += instance.spec.cpu;
                summary.memory += instance.spec.memory;
                summary.charges += instance.price.hourly;
                summary.hourly += instance.price.hourly;

                return summary;
            },
            {
                instances: 0,
                vcpus: 0,
                memory: 0,
                charges: 0,
                hourly: 0,
            }
        );
    }, [data]);

    return {
        data,
        isPending: isPending && !hasNoAccess,
        collective,
        refetch,
        isRefetching,
    };
}
